/* eslint-disable react/require-default-props */
import React from 'react';

import {
  FieldRoot,
  FieldLabel,
  RequiredMark,
  InputWrapper,
  StyledInput,
  IconSlot,
  InputShape,
  InputSize,
} from './styles';

/**
 * Input — campo de entrada de texto genérico do design system.
 *
 * Ícones via `leadingIcon` / `trailingIcon`. Busca completa usa `SearchBar` por cima.
 *
 * @example
 * <Input label="Nome completo" required placeholder="Digite" fullWidth />
 * <Input placeholder="Buscar por" trailingIcon={<SearchIcon />} aria-label="Buscar" />
 * <Input trailingIcon={<SearchIcon />} label="Buscar" required size="md" shape="pill" />
 */
export type InputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'size'
> & {
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  fullWidth?: boolean;
  className?: string;
  label?: React.ReactNode;
  required?: boolean;
  size?: InputSize;
  shape?: InputShape;
  /** Destaca a borda em vermelho (validação). @default false */
  error?: boolean;
};

let uidCounter = 0;

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      leadingIcon,
      trailingIcon,
      fullWidth = false,
      disabled = false,
      className,
      label,
      required = false,
      size = 'md',
      shape = 'pill',
      error = false,
      id,
      ...rest
    },
    ref
  ) => {
    const generatedId = React.useRef(`input-${(uidCounter += 1)}`).current;
    const inputId = id ?? (label ? generatedId : undefined);

    const field = (
      <InputWrapper
        as={label ? 'div' : 'label'}
        $disabled={disabled}
        $fullWidth={fullWidth}
        $size={size}
        $shape={shape}
        $error={error}
        className={label ? undefined : className}
      >
        {leadingIcon && <IconSlot $size={size}>{leadingIcon}</IconSlot>}
        <StyledInput
          ref={ref}
          id={inputId}
          disabled={disabled}
          aria-required={required || undefined}
          aria-invalid={error || undefined}
          $size={size}
          {...rest}
        />
        {trailingIcon && <IconSlot $size={size}>{trailingIcon}</IconSlot>}
      </InputWrapper>
    );

    if (!label) return field;

    return (
      <FieldRoot $fullWidth={fullWidth} className={className}>
        <FieldLabel htmlFor={inputId}>
          {label}
          {required && <RequiredMark aria-hidden="true"> *</RequiredMark>}
        </FieldLabel>
        {field}
      </FieldRoot>
    );
  }
);

export default Input;
